import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OptionGroups, PRODUCT_KEYS, ProductsChild, ProductsOption } from '@type/productType';
import {
  CallGetProductOptionGroup,
  CallGetProducts,
  CallPutConnectOptGroup,
  CallPutUnConnectOptGroup,
} from '@library/request/callProduct';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Spinner from '@components/molecules/Spinner';
import { numberWithCommas } from '@library/Utils';
import CommonEditIconButton from '@components/molecules/button/EditIconButton';
import CommonCheckBox from '@components/molecules/checkBox/CheckBox';
import useFixScroll from '@src/hooks/useFixScroll';
import { useMenuPermission } from '@src/recoil/permission';

// 옵션 연결
const ConnectOption = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const storeId = searchParams.get('storeID');
  const productID = searchParams.get('productID');

  const { isNoAuth2 } = useMenuPermission(storeId);

  const [loading, setLoading] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [optGroupState, setOptGroupState] = useState<OptionGroups[]>([]);

  useFixScroll(isPopupOpen); // 팝업 오픈 시 부모 페이지 스크롤 고정

  // 옵션그룹 리스트 조회 API
  const { data: productOptGroup } = useQuery([PRODUCT_KEYS.PRODUCTOPTIONGROUP, storeId, productID], {
    enabled: !!storeId && !!productID,
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetProductOptionGroup(storeId, productID);
        if (response?.result.code !== 1) return;
        const { data = [] } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // 메뉴 조회 API
  const { data: productDetail } = useQuery<ProductsOption[] | []>(
    [`${PRODUCT_KEYS.PRODUCT}Custom`, storeId, productID],
    {
      enabled: !!storeId && !!productID && Array.isArray(productOptGroup) && productOptGroup.length > 0,

      queryFn: async () => {
        try {
          setLoading(true);
          const response = await CallGetProducts(storeId, productID);
          if (response?.result.code !== 1) return;
          let optionGroups: ProductsOption[] | [] = [];
          const { data } = response;
          if (data) {
            optionGroups = data?.options?.filter((list) => Number(list.isPriceOption) === 0);
          }
          return optionGroups;
        } catch (error) {
          console.log('error', error);
          throw error;
        } finally {
          setLoading(false);
        }
      },
    },
  );

  // 옵션 연결 API
  const { mutate: connectOptMutation } = useMutation(
    (optionGroupID: string) => CallPutConnectOptGroup(storeId, optionGroupID, productID),
    {
      onSuccess: (response, optionGroupID) => {
        const { result } = response;
        if (result.code !== 1) {
          alert(result.message);
        } else {
          alert('옵션 연결이 완료되었습니다.');
          setOptGroupState((prev) =>
            prev.map((opt) => (opt.ID === Number(optionGroupID) ? { ...opt, link: opt.link === 1 ? 0 : 1 } : opt)),
          );
        }
      },
      onError: (error) => console.log('error:', error),
    },
  );

  // 옵션 해지 API
  const { mutate: unConnectOptMutation } = useMutation(
    ({ optionGroupID }: { optionGroupID: string; isReset?: boolean }) =>
      CallPutUnConnectOptGroup(storeId, optionGroupID, productID),
    {
      onMutate: () => setLoading(true),
      onSuccess: (response, variables) => {
        const { result } = response;
        const { optionGroupID, isReset } = variables;
        if (result.code !== 1) {
          alert(result.message);
        } else {
          alert('옵션 연결 해제가 완료되었습니다.');
          setOptGroupState((prev) =>
            prev.map((opt) => (opt.ID === Number(optionGroupID) ? { ...opt, link: opt.link === 1 ? 0 : 1 } : opt)),
          );
          if (isReset) {
            onResetCache();
          }
        }
      },
      onError: (error) => console.log('error:', error),
      onSettled: () => setLoading(false),
    },
  );

  const onChangeCheckBox = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isNoAuth2) return;
    const { id, checked } = e.target;
    if (checked === true) {
      connectOptMutation(id); // 옵션 연결
    } else {
      if (!window.confirm('연결을 해제하시겠습니까?')) return;
      unConnectOptMutation({ optionGroupID: id }); // 옵션 해지
    }
  };

  const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget;
    const wrapper = button.closest('.list-option');
    if (!wrapper) return;

    const isFolded = wrapper.classList.contains('fold');
    wrapper.classList.toggle('fold');
    button.textContent = isFolded ? '접기' : '더보기';
  };

  const spliceProducts = (products: string[] = [], max: number = 50): string => {
    if (products.length === 0) return '';

    const joined = products.join(', ');
    return joined.length > max ? `${joined.substring(0, max)}...` : joined;
  };

  const onResetCache = () => {
    queryClient.invalidateQueries([PRODUCT_KEYS.PRODUCTOPTIONGROUP, storeId, productID]); // 옵션연결 리스트 재조회
    queryClient.invalidateQueries([`${PRODUCT_KEYS.PRODUCT}Custom`, storeId, productID]); // 메뉴리스트 재조회
    queryClient.invalidateQueries([PRODUCT_KEYS.PRODUCT, storeId, productID]); // 메뉴 상세정보 > 옵션정보 리스트 초기화
  };

  useEffect(() => {
    if (productOptGroup) setOptGroupState(productOptGroup);
  }, [productOptGroup]);

  return (
    <article id="mArticle" className="art-product connect-option">
      <div className="view-product">
        <div className="box-product">
          <strong className="lab-product">옵션 설정</strong>
          {(!loading || productDetail) && (
            <div className="list-product-option">
              {optGroupState?.length > 0 ? (
                <>
                  {productDetail?.length > 0 ? (
                    <>
                      {productDetail.map((list: ProductsOption) => {
                        const { title, children, optionGroupID, isRequired } = list;
                        return (
                          <div className="item-product-option" key={list.optionGroupID}>
                            <strong className="tit-option">
                              {title} <span className="txt-count">(총 {children.length}개)</span>
                              {isRequired === 1 && <span className="red">*필수선택</span>}
                            </strong>
                            <CommonEditIconButton
                              onClick={() => {
                                if (!window.confirm('연결을 해제하시겠습니까?')) return;
                                unConnectOptMutation({ optionGroupID: optionGroupID.toString(), isReset: true }); // 옵션 해지
                              }}
                              title="해제"
                              iconName="check"
                              disable={isNoAuth2}
                            />
                            <div className="list-option fold">
                              <ul>
                                {children.map((child: ProductsChild) => (
                                  <li key={child.optionID}>
                                    {child.name || '기본'}
                                    {child.alcohol === '1' ? <span className="alcohol-badge">19</span> : ' '}:{' '}
                                    {numberWithCommas(child.price.toString() || '')}원
                                  </li>
                                ))}
                              </ul>
                              {children.length > 3 && (
                                <button type="button" className="btn-fold" onClick={handleToggle}>
                                  더보기
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <p id="emptyBox" className="empty-product-option">
                      연결된 옵션그룹이 없습니다.
                    </p>
                  )}
                  <button type="button" className="btn-round-add" onClick={() => setIsPopupOpen(true)} disabled={isNoAuth2}>
                    <span className="ico-plus"></span>추가
                  </button>
                </>
              ) : (
                <p className="empty-product-option">
                  옵션 그룹이 존재하지 않습니다.
                  <br />
                  옵션그룹 생성 후 연결해 주세요.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      {/* 옵션 추가 팝업 */}
      {isPopupOpen && (
        <div id="optionGroupsModal" className="biz-modal">
          <div className="inner-modal inner-connect-option">
            <div className="modal-head">
              <strong className="tit-modal">연결 옵션 선택</strong>
              <button
                type="button"
                className="ico-close"
                onClick={() => {
                  setIsPopupOpen(false);
                  onResetCache();
                }}
              />
            </div>
            <div className="modal-body">
              <p className="tip-product-option">
                <strong>메뉴에 연결할 옵션 그룹</strong>을 선택해 주세요.
                <br />
                옵션 정보 수정 및 새로운 옵션그룹 추가는{' '}
                <button
                  type="button"
                  id="optionListLink"
                  className="link-g"
                  onClick={() => navigate(`/product/list?tab=option&storeID=${storeId}`)}
                >
                  [옵션 관리]
                </button>{' '}
                메뉴에서 진행해 주세요.
              </p>
              {optGroupState?.length > 0 &&
                optGroupState.map((option) => {
                  const { title = '', children = [], link = 0, ID, products = [], isRequired } = option;
                  return (
                    <div className="item-product-option" key={ID}>
                      <strong className="tit-option">
                        {title} <span className="txt-count">(총 {children.length}개)</span>
                        {isRequired === 1 && <span className="red">*필수선택</span>}
                      </strong>
                      <CommonCheckBox id={ID.toString()} value={link === 1} onChange={onChangeCheckBox} disabled={isNoAuth2} />
                      <div className="list-option fold">
                        <ul>
                          {children.length > 0 &&
                            children.map((list) => (
                              <li key={list.optionID}>
                                {list.name}
                                {list.alcohol === 1 ? <span className="alcohol-badge">19</span> : ' '}:{' '}
                                {numberWithCommas(list.price.toString() || '')}원{' '}
                              </li>
                            ))}
                        </ul>
                        {children.length > 3 && (
                          <button type="button" className="btn-fold" onClick={handleToggle}>
                            더보기
                          </button>
                        )}
                      </div>
                      {products.length > 0 && (
                        <div className="box-connect-info">
                          <strong className="tit-connect-info">
                            <em className="badge-connect-info">연결메뉴</em> 해당 옵션과 연결되어 있는 메뉴입니다.
                          </strong>
                          <p className="desc-connect-info">{spliceProducts(products)}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}
      <Spinner show={loading} />
    </article>
  );
};

export default ConnectOption;
